import { RemoveCircleOutline } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import clsx from "clsx";
import { useState } from "react";
import { EditIcon } from "../../../../../assets/images/icons/EditIcon";
import PlusIcon from "../../../../../assets/images/icons/PlusIcon";
import { useBriaTheme } from "../../../../../hooks/useBriaTheme";
import { usePopoverPosition } from "../hooks/usePopoverPosition";
import styles from "../StructuredPromptImageCanvas.module.scss";
import { CanvasObject } from "../types";
import { hexToRgb, rgbToHex } from "../utils/colorUtils";
import FiboColorPicker from "./FiboColorPicker";

const PANEL_WIDTH = 120;

interface ColorPickerPopoverProps {
	object: CanvasObject;
	bbox: { left: number; top: number; width: number; height: number };
	canvasSize: { width: number; height: number };
	onColorChange: (objectId: string, colorIndex: number, color: { r: number; g: number; b: number }) => void;
	onAddColor: (objectId: string) => void;
	onRemoveColor: (objectId: string, colorIndex: number) => void;
}

export const ColorPickerPopover = ({
	object,
	bbox,
	canvasSize,
	onColorChange,
	onAddColor,
	onRemoveColor,
}: ColorPickerPopoverProps) => {
	const [isEditMode, setIsEditMode] = useState(false);
	const [newColorIndex, setNewColorIndex] = useState<number | null>(null);
	const { getCssVariableValue } = useBriaTheme();

	const estimatedHeight = object.colors.length * 28 + 60;
	const { left, top } = usePopoverPosition({ bbox, canvasSize, panelWidth: PANEL_WIDTH, estimatedHeight });

	return (
		<Box
			className={styles.colorPickerContainer}
			data-color-picker
			style={{ left: `${left}px`, top: `${top}px`, maxWidth: PANEL_WIDTH }}
		>
			<Box className={styles.colorPickerHeader}>
				<Typography className={styles.colorPickerContainerHeaderTitle}>Colors</Typography>
				<Box className={styles.colorPickerEditIcon} onClick={() => setIsEditMode(!isEditMode)}>
					<EditIcon className={styles.colorPickerEditIcon} />
				</Box>
			</Box>

			{object.colors.map((color, index) => {
				const hexColor = rgbToHex(color.r, color.g, color.b);
				return (
					<Box
						key={index}
						className={clsx(styles.colorPickerRow, isEditMode && styles.colorPickerRowEditMode)}
					>
						{isEditMode && (
							<Box className={styles.colorRemoveIcon} onClick={() => onRemoveColor(object.id, index)}>
								<RemoveCircleOutline sx={{ fontSize: 14 }} />
							</Box>
						)}
						<FiboColorPicker
							mode="hex"
							color={hexColor}
							autoOpen={index === newColorIndex}
							onChange={(newHex) => {
								setNewColorIndex(null);
								const newRgb = hexToRgb(newHex);
								if (newRgb) onColorChange(object.id, index, newRgb);
							}}
						/>
					</Box>
				);
			})}

			<Box
				className={styles.colorAddIcon}
				onClick={() => {
					setNewColorIndex(object.colors.length);
					onAddColor(object.id);
				}}
			>
				<PlusIcon fillColor={getCssVariableValue("--black-color")} height="8" />
			</Box>
		</Box>
	);
};
